import React from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  Create as PenIcon,
  Undo as UndoIcon,
  DeleteOutline as ClearIcon,
} from '@mui/icons-material';
import { DrawToolbarState, PRESET_COLORS, LINE_WIDTHS } from './types';

interface DrawingToolbarProps {
  toolbarState: DrawToolbarState;
  onToolbarStateChange: (state: DrawToolbarState) => void;
  onUndo: () => void;
  onClearAll: () => void;
  hasStrokes: boolean;
}

const DrawingToolbar: React.FC<DrawingToolbarProps> = ({
  toolbarState,
  onToolbarStateChange,
  onUndo,
  onClearAll,
  hasStrokes,
}) => {
  const { isDrawMode, activeColor, lineWidth, activeTool } = toolbarState;

  const update = (partial: Partial<DrawToolbarState>) =>
    onToolbarStateChange({ ...toolbarState, ...partial });

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {/* Draw mode toggle */}
      <Tooltip title={isDrawMode ? 'Stop Drawing' : 'Draw'}>
        <IconButton
          size="small"
          onClick={() => update({ isDrawMode: !isDrawMode })}
          sx={{
            bgcolor: isDrawMode ? 'primary.main' : 'transparent',
            color: isDrawMode ? 'primary.contrastText' : 'inherit',
            '&:hover': {
              bgcolor: isDrawMode ? 'primary.dark' : 'action.hover',
            },
          }}
        >
          <PenIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      {isDrawMode && (
        <>
          {/* Separator */}
          <Box sx={{ width: 1, height: 20, bgcolor: 'divider', mx: 0.25 }} />

          {/* Tool: Pen vs Eraser */}
          <Tooltip title="Pen">
            <IconButton
              size="small"
              onClick={() => update({ activeTool: 'pen' })}
              sx={{
                bgcolor: activeTool === 'pen' ? 'rgba(255,255,255,0.15)' : 'transparent',
                border: activeTool === 'pen' ? '1px solid rgba(255,255,255,0.3)' : '1px solid transparent',
              }}
            >
              <PenIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eraser">
            <IconButton
              size="small"
              onClick={() => update({ activeTool: 'eraser' })}
              sx={{
                bgcolor: activeTool === 'eraser' ? 'rgba(255,255,255,0.15)' : 'transparent',
                border: activeTool === 'eraser' ? '1px solid rgba(255,255,255,0.3)' : '1px solid transparent',
                fontSize: 14,
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 11 }}>E</Typography>
            </IconButton>
          </Tooltip>

          {/* Separator */}
          <Box sx={{ width: 1, height: 20, bgcolor: 'divider', mx: 0.25 }} />

          {/* Colors */}
          {PRESET_COLORS.map((color) => (
            <Tooltip key={color} title={color}>
              <IconButton
                size="small"
                onClick={() => update({ activeColor: color, activeTool: 'pen' })}
                sx={{ p: 0.25 }}
              >
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    bgcolor: color,
                    border: activeColor === color && activeTool === 'pen'
                      ? '2px solid rgba(255,255,255,0.9)'
                      : '1px solid rgba(255,255,255,0.3)',
                    boxShadow: activeColor === color && activeTool === 'pen'
                      ? '0 0 4px rgba(255,255,255,0.5)'
                      : 'none',
                  }}
                />
              </IconButton>
            </Tooltip>
          ))}

          {/* Separator */}
          <Box sx={{ width: 1, height: 20, bgcolor: 'divider', mx: 0.25 }} />

          {/* Line widths */}
          {LINE_WIDTHS.map((lw) => (
            <Tooltip key={lw.value} title={lw.label}>
              <IconButton
                size="small"
                onClick={() => update({ lineWidth: lw.value })}
                sx={{
                  p: 0.25,
                  bgcolor: lineWidth === lw.value ? 'rgba(255,255,255,0.15)' : 'transparent',
                  border: lineWidth === lw.value ? '1px solid rgba(255,255,255,0.3)' : '1px solid transparent',
                }}
              >
                <Box
                  sx={{
                    width: 18,
                    height: lw.value,
                    borderRadius: lw.value / 2,
                    bgcolor: 'currentColor',
                  }}
                />
              </IconButton>
            </Tooltip>
          ))}

          {/* Separator */}
          <Box sx={{ width: 1, height: 20, bgcolor: 'divider', mx: 0.25 }} />

          {/* Undo */}
          <Tooltip title="Undo">
            <span>
              <IconButton size="small" onClick={onUndo} disabled={!hasStrokes}>
                <UndoIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          {/* Clear All */}
          <Tooltip title="Clear All">
            <span>
              <IconButton size="small" onClick={onClearAll} disabled={!hasStrokes}>
                <ClearIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </>
      )}
    </Box>
  );
};

export default DrawingToolbar;
